import { Link } from "react-router-dom";
import { Button, Card } from "../components";

type PublicInfoKind = "privacy" | "terms" | "contact";

interface PublicInfoPageProps {
  kind: PublicInfoKind;
}

const pages: Record<
  PublicInfoKind,
  {
    eyebrow: string;
    title: string;
    summary: string;
    sections: Array<{ heading: string; body: string }>;
    updated: string;
  }
> = {
  privacy: {
    eyebrow: "Privacy notice",
    title: "Privacy Notice",
    summary:
      "Agent Me is in controlled early access. This notice explains what we collect for waitlist, account setup, provisioning, support, billing, and service operation.",
    updated: "April 24, 2026",
    sections: [
      {
        heading: "Information we collect",
        body: "We collect the email address you submit to the waitlist, account details you provide during registration, operational telemetry needed to run your agent workspace, and support messages you choose to send us.",
      },
      {
        heading: "How we use it",
        body: "We use this information to process waitlist requests, approve access, provision tenant workspaces, deliver account emails, monitor reliability, prevent abuse, and provide support.",
      },
      {
        heading: "Inference and workspace data",
        body: "When your workspace uses platform-managed inference, Agent Me routes requests through the configured platform inference provider and applies platform budget controls. Workspace content is used to operate your requested agent features, not to sell advertising.",
      },
      {
        heading: "Retention and choices",
        body: "You can ask us to remove a waitlist entry or support conversation by contacting the operator mailbox. Production retention controls will be tightened before broad public launch.",
      },
    ],
  },
  terms: {
    eyebrow: "Early access terms",
    title: "Terms of Use",
    summary:
      "These early access terms describe the current private-launch posture for Agent Me while provisioning, billing, and workspace features are still being certified.",
    updated: "April 24, 2026",
    sections: [
      {
        heading: "Early access service",
        body: "Agent Me is currently offered as an early access service. Access may be approved, paused, or revoked while we complete launch readiness, security review, billing certification, and operational hardening.",
      },
      {
        heading: "Acceptable use",
        body: "Do not use Agent Me to break the law, harm others, abuse infrastructure, bypass security controls, or submit data you do not have the right to process.",
      },
      {
        heading: "Billing and platform inference",
        body: "Some workspaces may use platform-managed inference budgets. Paid billing remains subject to the live billing configuration and checkout contract when enabled by the operator.",
      },
      {
        heading: "No production guarantee yet",
        body: "During early access, features may change and availability may be interrupted for testing, remediation, and upgrade work. Launch-critical guarantees are tracked through the Agent Me readiness process.",
      },
    ],
  },
  contact: {
    eyebrow: "Support contact",
    title: "Contact Agent Me",
    summary:
      "Use this page when you need help with waitlist status, account setup, privacy requests, billing readiness, or workspace support.",
    updated: "April 24, 2026",
    sections: [
      {
        heading: "Waitlist and account help",
        body: "If you have submitted the waitlist form and have not received a welcome email, contact the operator with the email address you used to request access.",
      },
      {
        heading: "Privacy requests",
        body: "For privacy or data deletion requests, include the email address associated with the waitlist or workspace so we can locate the relevant record.",
      },
      {
        heading: "Operational support",
        body: "For workspace support, include your workspace URL, tenant name, approximate time of the issue, and a short description of what you expected to happen.",
      },
    ],
  },
};

export default function PublicInfoPage({ kind }: PublicInfoPageProps) {
  const page = pages[kind];

  return (
    <main
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--c-surface-2)",
        padding: "2rem 1rem",
      }}
    >
      <div style={{ width: "100%", maxWidth: 760, margin: "0 auto" }}>
        <Card>
          <div style={{ marginBottom: "1.5rem" }}>
            <p
              style={{
                margin: "0 0 0.4rem",
                color: "var(--c-text-faint)",
                fontSize: "0.75rem",
                textTransform: "uppercase",
              }}
            >
              {page.eyebrow}
            </p>
            <h1
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: "2.25rem",
                fontWeight: 400,
                color: "var(--c-text)",
                margin: "0 0 0.75rem",
              }}
            >
              {page.title}
            </h1>
            <p style={{ color: "var(--c-text-secondary)", margin: 0 }}>{page.summary}</p>
            <p style={{ color: "var(--c-text-faint)", fontSize: "0.8rem", margin: "0.75rem 0 0" }}>
              Last updated: {page.updated}
            </p>
          </div>

          <div style={{ display: "grid", gap: "1rem" }}>
            {page.sections.map((section) => (
              <section key={section.heading}>
                <h2 style={{ color: "var(--c-text)", fontSize: "1rem", margin: "0 0 0.35rem" }}>
                  {section.heading}
                </h2>
                <p style={{ color: "var(--c-text-secondary)", margin: 0 }}>{section.body}</p>
              </section>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              flexWrap: "wrap",
              marginTop: "1.5rem",
            }}
          >
            <Link to="/waitlist" style={{ textDecoration: "none" }}>
              <Button variant="accent">Join waitlist</Button>
            </Link>
            {kind !== "contact" ? (
              <Link to="/contact" style={{ textDecoration: "none" }}>
                <Button variant="ghost">Contact support</Button>
              </Link>
            ) : null}
          </div>
        </Card>
      </div>
    </main>
  );
}
